import { useMemo } from "react";
import { CoreCarsComponent, initialFiltersState } from "@b2bportal/core-cars";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FilterCarType,
  useFilterList,
  FilterCancellation,
  FilterSuppliers,
  FilterBags,
  FilterPassenger,
  FilterPrice,
} from "@tenant/cars";
import { FilterPopover } from "@tenant/ui";
import clsx from "clsx";
import styles from "./styles.module.scss";

export type FilterListProps = PlatformComponentProps;

const COMPONENT_KEY = CoreCarsComponent.FilterList;

export const FilterList = ({ className, children }: FilterListProps) => {
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const { context, handlers } = useFilterList();

  const hasBagFilter = useMemo(
    () => context.filters.minimumBags > initialFiltersState.minimumBags,
    [context.filters.minimumBags]
  );
  return (
    <div className={clsx(block, className)}>
      <FilterPopover
        label={
          context.filters.carType.length !== initialFiltersState.carType.length
            ? context.filters.carType.length > 1
              ? t("core-cars.filters.type.filterAppliedTitle.multipleApplied", {
                  type: context.filters.carType[0],
                  count: context.filters.carType.length - 1,
                })
              : t("core-cars.filters.type.filterAppliedTitle.oneApplied", {
                  type: context.filters.carType[0],
                })
            : t("core-cars.filters.type.title")
        }
        disabled={context.disabled}
        popoverClassName={cn("filter-popover")}
        isApplied={context.filters.carType.length > 0}
        onReset={handlers.resetFilter.resetCarTypeFilter}
      >
        <FilterCarType
          selectedValues={context.filters.carType}
          onChange={handlers.handleCarTypeFilter}
          className={cn("car-type")}
        />
      </FilterPopover>
      <FilterPopover
        label={
          context.filters.priceRange.max !==
            initialFiltersState.priceRange?.max ||
          context.filters.priceRange.min !== initialFiltersState.priceRange?.min
            ? t("core-cars.filters.price.filterAppliedTitle", {
                currency:
                  context.filterOptions.priceRange?.max.currencySymbol ?? "$",
                filterAmt: Math.ceil(context.filters.priceRange.max),
              })
            : t("core-cars.filters.price.title")
        }
        disabled={context.disabled}
        popoverClassName={cn("filter-popover")}
        isApplied={
          context.filters.priceRange.max !== initialFiltersState.priceRange?.max
        }
        onReset={handlers.resetFilter.resetPriceFilter}
      >
        <FilterPrice
          onChange={handlers.handlePriceRangeFilter}
          range={context.filterOptions.priceRange ?? undefined}
          selectedRange={context.filters.priceRange}
          label={t("core-cars.filters.price.title")}
          className={cn("price")}
        />
      </FilterPopover>
      <FilterPopover
        label={
          context.filters.supplier.length !==
          initialFiltersState.supplier.length
            ? context.filters.supplier.length > 1
              ? t(
                  "core-cars.filters.rentalCompany.filterAppliedTitle.multipleApplied",
                  {
                    company: context.filters.supplier[0].name,
                    count: context.filters.supplier.length - 1,
                  }
                )
              : t(
                  "core-cars.filters.rentalCompany.filterAppliedTitle.oneApplied",
                  {
                    company: context.filters.supplier[0].name,
                  }
                )
            : t("core-cars.filters.rentalCompany.title")
        }
        disabled={context.disabled}
        popoverClassName={cn("filter-popover")}
        isApplied={context.filters.supplier.length > 0}
        onReset={handlers.resetFilter.resetSupplierFilter}
      >
        <FilterSuppliers
          selectedValues={context.filters.supplier}
          onChange={handlers.handleSupplierFilter}
          options={context.filterOptions.suppliers}
          className={cn("suppliers")}
        />
      </FilterPopover>
      <FilterPopover
        label={
          context.filters.freeCancellation
            ? t("core-cars.filters.cancellation.filterAppliedTitle")
            : t("core-cars.filters.cancellation.title")
        }
        disabled={context.disabled}
        popoverClassName={cn("filter-popover")}
        isApplied={context.filters.freeCancellation}
        onReset={handlers.resetFilter.resetCancellation}
      >
        <FilterCancellation
          isFreeCancellationSelected={context.filters.freeCancellation}
          onChange={handlers.handleCancellationFilter}
          className={cn("cancellation")}
        />
      </FilterPopover>
      <FilterPopover
        label={
          context.filters.passenger.length > 0
            ? context.filters.passenger.includes("SixOrMore")
              ? t("core-cars.filters.passengers.filterAppliedTitle.sixOrMore")
              : t("core-cars.filters.passengers.filterAppliedTitle.upToFive")
            : t("core-cars.filters.passengers.title")
        }
        disabled={context.disabled}
        popoverClassName={cn("filter-popover")}
        isApplied={context.filters.passenger.length > 0}
        onReset={handlers.resetFilter.resetPassengerFilter}
      >
        <FilterPassenger
          selectedOption={context.filters.passenger}
          onChange={handlers.handlePassengerFilter}
          className={cn("passenger")}
        />
      </FilterPopover>
      <FilterPopover
        label={
          hasBagFilter
            ? t("core-cars.filters.minimumBags.filterAppliedTitle", {
                minCount: context.filters.minimumBags,
              })
            : t("core-cars.filters.minimumBags.title")
        }
        disabled={context.disabled}
        popoverClassName={cn("filter-popover")}
        isApplied={hasBagFilter}
        onReset={handlers.resetFilter.resetMinimumBagsFilter}
      >
        <FilterBags
          minimumBags={context.filters.minimumBags}
          onChange={handlers.handleMinimumBagsFilter}
          className={cn("minimum-bags")}
        />
      </FilterPopover>

      {children}
    </div>
  );
};
